import { useNavigate, useParams } from "react-router";
import { Stack, Button, Select, Option, IconButton } from "@introist/react-foundation/v2";
import FeatherIcon from "feather-icons-react";
import { PublicForm } from "../components/PublicForm/PublicForm";
import {
  AlreadySubmitted,
  ErrorForm,
  FormSubmitted,
  LoadingForm
} from "../components/PublicForm/StateViews";
import { useState } from "react";
import { useForm, usePreviewForm } from "../useForms";
import { FormPreviewDrawer } from "../components/FormPreview/FormPreviewDrawer";

type PreviewMode = "form" | "loading" | "error" | "submitted" | "already-submitted";

interface FormBranding {
  backgroundColor?: string;
  primaryColor?: string;
  logoUrl?: string;
  backgroundImageUrl?: string;
  backgroundImageMode?: "full" | "banner";
  fontFamily?: string | null;
}

export const FormPreviewRoute = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { form: preview } = usePreviewForm(id!);
  const { form } = useForm(id!);

  const [drawerOpen, setDrawerOpen] = useState(true);
  const [previewMode, setPreviewMode] = useState<PreviewMode>("form");
  const [livePreviewBranding, setLivePreviewBranding] = useState<FormBranding | undefined>(
    undefined
  );
  const [livePreviewWelcomeScreen, setLivePreviewWelcomeScreen] = useState<
    { title: string; description: string } | undefined
  >(undefined);

  const renderPreview = () => {
    // Get current branding (live preview or saved)
    const currentBranding = livePreviewBranding || (preview as any)?.branding;
    // Get current welcome screen (live preview or saved)
    const currentWelcomeScreen = livePreviewWelcomeScreen || (preview as any)?.welcomeScreen;

    switch (previewMode) {
      case "loading":
        return <LoadingForm />;
      case "submitted":
        return <FormSubmitted branding={currentBranding} />;
      case "already-submitted":
        return <AlreadySubmitted branding={currentBranding} />;
      case "error":
        return <ErrorForm />;
      case "form":
      default:
        if (!preview) {
          return <LoadingForm />;
        }
        // Merge live preview branding and welcome screen with the form data
        const previewWithChanges = {
          ...preview,
          branding: currentBranding,
          welcomeScreen: currentWelcomeScreen
        };
        return (
          <PublicForm
            form={previewWithChanges as any}
            onSubmit={() => {
              // Preview mode - change to thank you view
              setPreviewMode("submitted");
              return Promise.resolve();
            }}
            responseId="preview"
            previewMode={true}
          />
        );
    }
  };

  return (
    <div style={{ display: "flex", height: "100vh", position: "relative" }}>
      {/* Main content area - form preview */}
      <div style={{ flex: 1, overflow: "auto", position: "relative" }}>
        {renderPreview()}

        {/* Back to Form button - top left */}
        <Button
          size="small"
          variant="outlined"
          startIcon="arrowLeft"
          onClick={() => navigate(`/forms/${id}`)}
          style={{
            position: "absolute",
            top: "20px",
            left: "20px",
            zIndex: 1000
          }}
        >
          Back to Introist
        </Button>

        {/* Top controls - Display mode and drawer toggle */}
        <Stack
          gap="small"
          style={{
            position: "absolute",
            top: "20px",
            right: "20px",
            zIndex: 1000
          }}
        >
          <Select
            element="button"
            variant="outlined"
            value={previewMode}
            onSelect={(option: Option) => setPreviewMode(option.key as PreviewMode)}
            options={[
              { key: "loading", title: "Loading" },
              { key: "form", title: "Form" },
              { key: "submitted", title: "Thank You" },
              { key: "already-submitted", title: "Already Submitted" },
              { key: "error", title: "Error" }
            ]}
            attachToRef={false}
            size="small"
            startAdornment={
              <span style={{ color: "black", display: "flex", alignItems: "center" }}>
                <FeatherIcon icon="layers" size={16} />
              </span>
            }
          />
          <IconButton
            size="large"
            variant="outlined"
            icon={drawerOpen ? "chevronRight" : "chevronLeft"}
            onClick={() => setDrawerOpen(!drawerOpen)}
            aria-label={drawerOpen ? "Hide settings" : "Show settings"}
          />
        </Stack>
      </div>

      {/* Right-hand side settings panel */}
      <FormPreviewDrawer
        drawerOpen={drawerOpen}
        formId={id!}
        form={form}
        onWelcomeScreenChange={setLivePreviewWelcomeScreen}
        onBrandingChange={setLivePreviewBranding}
        onSaveComplete={() => {
          setLivePreviewWelcomeScreen(undefined);
          setLivePreviewBranding(undefined);
        }}
      />
    </div>
  );
};
